import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraBadge } from '../ensigns/badge';
import { chartHatchBackground, chartToneBorderClass, chartToneInk, chartToneVar } from '../ensigns/chart-tone';
import { SpecraEyebrow } from '../ensigns/eyebrow';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraSwatch } from '../ensigns/swatch';
import { SpecraText } from '../ensigns/typography';
import { SpecraChartFrame, SpecraChartLegend, SpecraChartLink } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
/** Returns t → percent (0–100, clamped) honouring `stops`. */ export function swimlaneScale(scale) {
    const { start, end } = scale;
    const stops = [
        ...scale.stops ?? []
    ].sort((a, b)=>a.at - b.at);
    const bounds = [
        start,
        ...stops.map((s)=>s.at).filter((a)=>a > start && a < end).sort((a, b)=>a - b),
        end
    ];
    const weightAt = (t)=>{
        let w = 1;
        for (const s of stops)if (s.at <= t) w = s.weight;
        return w;
    };
    const weights = bounds.slice(0, -1).map((b)=>weightAt(b));
    const total = bounds.slice(0, -1).reduce((n, b, i)=>n + (bounds[i + 1] - b) * weights[i], 0);
    return (t)=>{
        if (!(total > 0)) return 0;
        const c = Math.min(end, Math.max(start, t));
        let acc = 0;
        for(let i = 0; i < bounds.length - 1; i++){
            const lo = bounds[i];
            const hi = bounds[i + 1];
            if (c >= hi) acc += (hi - lo) * weights[i];
            else {
                acc += (c - lo) * weights[i];
                break;
            }
        }
        return acc / total * 100;
    };
}
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
/** 2026.79 → "Oct 2026"; 2019 → "Jan 2019". months = Math.min(11, Math.floor((t - year) * 12 + 1e-6)). */ export function formatSwimTime(t) {
    const year = Math.floor(t);
    const month = Math.min(11, Math.floor((t - year) * 12 + 1e-6));
    return `${MONTHS[month]} ${year}`;
}
const segmentTitle = (seg, fmt)=>seg.title ?? `${seg.label}, ${fmt(seg.start)} to ${fmt(seg.end)}`;
const LINE_STYLE = {
    solid: 'border-solid',
    dashed: 'border-dashed',
    dotted: 'border-dotted'
};
const laneLabel = (lane)=>lane.sublabel ? `${lane.label} · ${lane.sublabel}` : lane.label;
/** Lanes of labelled contract bars on a (piecewise) year axis, with a now rule. */ export function SpecraSwimlaneTimeline({ scale, bands, periods, nowLabel, density = 'default', legend, table, formatTime = formatSwimTime, emptyMessage, renderLink, className, getLaneTestId, getSegmentTestId, selectedPeriodId, onPeriodSelect, selectedStationId, onStationSelect, getStationTestId, title, description, actions, insight, 'data-testid': testId }) {
    const pos = React.useMemo(()=>swimlaneScale(scale), [
        scale
    ]);
    const laneHeightVar = density === 'compact' ? 'var(--component-swimlane-lane-height-compact)' : 'var(--component-swimlane-lane-height)';
    const laneH = density === 'compact' ? 'h-[var(--component-swimlane-lane-height-compact)]' : 'h-[var(--component-swimlane-lane-height)]';
    const empty = bands.every((b)=>(b.kind === 'density' ? b.dots : b.lanes).length === 0);
    const now = scale.now;
    const selectedPeriod = periods.find((p)=>p.id === selectedPeriodId);
    const autoTable = {
        columns: [
            {
                key: 'lane',
                label: 'Seat'
            },
            {
                key: 'item',
                label: 'Held by / item'
            },
            {
                key: 'from',
                label: 'From'
            },
            {
                key: 'to',
                label: 'To'
            }
        ],
        rows: bands.flatMap((b)=>[
                ...(b.lanes ?? []).flatMap((lane)=>[
                        ...lane.segments.map((seg)=>({
                                id: `${lane.id}:${seg.id}`,
                                cells: {
                                    lane: laneLabel(lane),
                                    item: seg.label,
                                    from: formatTime(seg.start),
                                    to: formatTime(seg.end)
                                }
                            })),
                        ...(lane.stations ?? []).map((s)=>({
                                id: `${lane.id}:station:${s.id}`,
                                cells: {
                                    lane: laneLabel(lane),
                                    item: s.title,
                                    from: formatTime(s.at),
                                    to: ''
                                }
                            }))
                    ]),
                ...(b.dots ?? []).map((d)=>({
                        id: `${b.id}:dot:${d.id}`,
                        cells: {
                            lane: b.label ?? '',
                            item: d.title,
                            from: formatTime(d.at),
                            to: ''
                        }
                    }))
            ])
    };
    const header = /*#__PURE__*/ _jsx("div", {
        className: "relative h-[var(--component-swimlane-header-height)] border-b border-foreground",
        children: periods.map((p)=>{
            const style = {
                left: `${pos(p.start)}%`,
                width: `${pos(p.end) - pos(p.start)}%`
            };
            if (onPeriodSelect) {
                const selected = selectedPeriodId === p.id;
                return /*#__PURE__*/ _jsx("button", {
                    "aria-pressed": selected,
                    className: cn('absolute top-0 flex h-full items-center border-l border-[color:var(--chart-grid)] pl-[var(--space-1)] text-left text-[length:var(--fs-ui-meta)] font-medium', specraFocusRing, selected && 'bg-[color:var(--surface-sunken)] font-semibold'),
                    "data-period-id": p.id,
                    onClick: ()=>onPeriodSelect(p.id),
                    style: style,
                    type: "button",
                    children: p.label
                }, p.id);
            }
            return /*#__PURE__*/ _jsx(SpecraText, {
                className: "absolute top-0 flex h-full items-center border-l border-[color:var(--chart-grid)] pl-[var(--space-1)] font-medium",
                "data-period-id": p.id,
                size: "meta",
                style: style,
                children: p.label
            }, p.id);
        })
    });
    return /*#__PURE__*/ _jsx(SpecraChartFrame, {
        actions: actions,
        className: className,
        "data-testid": testId,
        description: description,
        insight: empty ? undefined : insight,
        legend: empty ? undefined : /*#__PURE__*/ _jsx(SpecraChartLegend, {
            items: legend
        }),
        table: table ?? autoTable,
        title: title,
        children: empty ? /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            "data-chart-empty": "",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "w-[var(--component-swimlane-label-width)] shrink-0 pr-[var(--space-3)]",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "h-[var(--component-swimlane-header-height)] border-b border-foreground"
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "min-w-0 flex-1",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "h-[var(--component-swimlane-header-height)] border-b border-foreground"
                            })
                        })
                    ]
                }),
                [
                    20,
                    45,
                    30
                ].map((w)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex h-[var(--component-swimlane-lane-height-compact)] items-center border-b border-[color:var(--border-subtle)]",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "h-2 w-[var(--component-swimlane-label-width)] shrink-0 rounded-[var(--radius-xs)] bg-[var(--chart-heat-0)]"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "ml-[var(--space-3)] min-w-0 flex-1",
                                children: /*#__PURE__*/ _jsx("span", {
                                    className: "block h-[var(--component-swimlane-segment-height)] rounded-[var(--radius-xs)] bg-[var(--chart-heat-0)]",
                                    style: {
                                        width: `${w}%`
                                    }
                                })
                            })
                        ]
                    }, w)),
                /*#__PURE__*/ _jsx("div", {
                    className: "absolute inset-0 flex items-center justify-center",
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "max-w-sm rounded-[var(--radius-md)] bg-[var(--surface-card)] px-[var(--space-2)] py-[var(--space-1)] text-center",
                        size: "body-ui",
                        tone: "muted",
                        children: emptyMessage
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsx(SpecraTooltipProvider, {
            delayDuration: 100,
            children: /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex min-w-[var(--component-swimlane-min-width)]",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "w-[var(--component-swimlane-label-width)] shrink-0 pr-[var(--space-3)]",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "h-[var(--component-swimlane-header-height)] border-b border-foreground"
                                }),
                                bands.map((band)=>band.kind === 'density' ? /*#__PURE__*/ _jsx("div", {
                                        className: "flex h-[var(--component-swimlane-band-height)] items-center border-b border-[color:var(--border-subtle)]",
                                        "data-band-id": band.id,
                                        children: /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                            children: band.label
                                        })
                                    }, band.id) : /*#__PURE__*/ _jsxs(React.Fragment, {
                                        children: [
                                            band.label ? /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                                className: "flex h-[var(--space-6)] items-center",
                                                children: band.label
                                            }) : null,
                                            (band.lanes ?? []).map((lane)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: cn(laneH, 'flex flex-col justify-center border-b border-[color:var(--border-subtle)]', lane.dimmed && 'opacity-40'),
                                                    "data-item-id": lane.id,
                                                    "data-lane-label": "",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(SpecraText, {
                                                            className: "truncate font-medium",
                                                            size: "body-sm",
                                                            title: lane.label,
                                                            children: lane.label
                                                        }),
                                                        lane.sublabel ? /*#__PURE__*/ _jsx(SpecraText, {
                                                            className: "truncate",
                                                            size: "meta",
                                                            title: lane.sublabel,
                                                            tone: "muted",
                                                            children: lane.sublabel
                                                        }) : null
                                                    ]
                                                }, lane.id))
                                        ]
                                    }, band.id))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "relative min-w-0 flex-1",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    "aria-hidden": "true",
                                    className: "pointer-events-none absolute inset-0",
                                    "data-chart-overlay": "",
                                    children: [
                                        selectedPeriod ? /*#__PURE__*/ _jsx("span", {
                                            className: "absolute inset-y-0 bg-[color:var(--surface-sunken)]",
                                            "data-chart-selected-period": selectedPeriod.id,
                                            style: {
                                                left: `${pos(selectedPeriod.start)}%`,
                                                width: `${pos(selectedPeriod.end) - pos(selectedPeriod.start)}%`
                                            }
                                        }) : null,
                                        periods.map((p)=>/*#__PURE__*/ _jsx("span", {
                                                className: "absolute inset-y-0 border-l border-[color:var(--chart-grid)]",
                                                style: {
                                                    left: `${pos(p.start)}%`
                                                }
                                            }, p.id)),
                                        now !== undefined ? /*#__PURE__*/ _jsx("span", {
                                            className: "absolute inset-y-0 right-0 bg-[color:var(--surface-sunken)] opacity-50",
                                            "data-chart-future": "",
                                            style: {
                                                left: `${pos(now)}%`
                                            }
                                        }) : null
                                    ]
                                }),
                                header,
                                bands.map((band)=>band.kind === 'density' ? /*#__PURE__*/ _jsx("div", {
                                        className: "relative h-[var(--component-swimlane-band-height)] border-b border-[color:var(--border-subtle)]",
                                        "data-band-id": band.id,
                                        "data-density-track": "",
                                        children: (band.dots ?? []).map((d)=>/*#__PURE__*/ _jsx(SpecraSwatch, {
                                                "aria-hidden": "true",
                                                className: "absolute -translate-x-1/2",
                                                "data-item-id": d.id,
                                                shape: "dot",
                                                style: {
                                                    left: `${pos(d.at)}%`,
                                                    top: `calc(var(--space-2) + ${d.slot ?? 0} * var(--space-3))`
                                                },
                                                tone: d.tone
                                            }, d.id))
                                    }, band.id) : /*#__PURE__*/ _jsxs(React.Fragment, {
                                        children: [
                                            band.label ? /*#__PURE__*/ _jsx("div", {
                                                "aria-hidden": "true",
                                                className: "h-[var(--space-6)]"
                                            }) : null,
                                            (band.lanes ?? []).map((lane, laneIndex, lanes)=>/*#__PURE__*/ _jsx(LaneTrack, {
                                                    density: density,
                                                    formatTime: formatTime,
                                                    getSegmentTestId: getSegmentTestId,
                                                    getStationTestId: getStationTestId,
                                                    lane: lane,
                                                    laneH: laneH,
                                                    laneHeightVar: laneHeightVar,
                                                    laneIndex: laneIndex,
                                                    lanes: lanes,
                                                    onStationSelect: onStationSelect,
                                                    pos: pos,
                                                    renderLink: renderLink,
                                                    selectedStationId: selectedStationId,
                                                    testId: getLaneTestId?.(lane)
                                                }, lane.id))
                                        ]
                                    }, band.id)),
                                now !== undefined ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            "aria-hidden": "true",
                                            className: "pointer-events-none absolute inset-y-0 w-0 border-l-2 border-foreground",
                                            "data-chart-now": "",
                                            style: {
                                                left: `${pos(now)}%`
                                            }
                                        }),
                                        nowLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                            className: "absolute top-[var(--component-swimlane-header-height)] -translate-x-1/2 whitespace-nowrap",
                                            "data-chart-now-label": "",
                                            style: {
                                                left: `${pos(now)}%`
                                            },
                                            variant: "dark",
                                            children: nowLabel
                                        }) : null
                                    ]
                                }) : null
                            ]
                        })
                    ]
                })
            })
        })
    });
}
function LaneTrack({ lane, laneIndex, lanes, laneH, laneHeightVar, density, pos, formatTime, renderLink, testId, getSegmentTestId, getStationTestId, selectedStationId, onStationSelect }) {
    const segments = [
        ...lane.segments
    ].sort((a, b)=>a.start - b.start);
    const stations = [
        ...lane.stations ?? []
    ].sort((a, b)=>a.at - b.at);
    const dimmed = Boolean(lane.dimmed);
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(laneH, 'relative border-b border-[color:var(--border-subtle)]', dimmed && 'opacity-40'),
        "data-item-id": lane.id,
        "data-lane-track": "",
        "data-testid": testId,
        children: [
            (lane.threads ?? []).map((t)=>{
                const target = t.connectTo ? lanes.findIndex((l)=>l.id === t.connectTo?.laneId) : -1;
                const delta = target >= 0 ? target - laneIndex : 0;
                return /*#__PURE__*/ _jsxs(React.Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": "true",
                            className: cn('absolute top-[var(--space-6)] h-0 border-t-2', LINE_STYLE[t.style], chartToneBorderClass[t.tone]),
                            "data-thread": t.style,
                            style: {
                                left: `${pos(t.from)}%`,
                                width: `${pos(t.to) - pos(t.from)}%`
                            }
                        }),
                        t.label ? /*#__PURE__*/ _jsxs(SpecraText, {
                            className: "absolute top-[var(--space-1)] whitespace-nowrap",
                            size: "meta",
                            style: {
                                left: `${pos(t.from)}%`
                            },
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-foreground",
                                    children: t.label
                                }),
                                t.sublabel ? /*#__PURE__*/ _jsxs("span", {
                                    className: "text-muted-foreground",
                                    children: [
                                        " · ",
                                        t.sublabel
                                    ]
                                }) : null
                            ]
                        }) : null,
                        t.connectTo && delta !== 0 ? /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": "true",
                            className: cn('absolute w-0 border-l-2', LINE_STYLE[t.style], chartToneBorderClass[t.tone]),
                            "data-thread-connector": "",
                            style: {
                                left: `${pos(t.connectTo.at)}%`,
                                top: delta > 0 ? 'var(--space-6)' : `calc(var(--space-6) - ${-delta} * ${laneHeightVar})`,
                                height: `calc(${Math.abs(delta)} * ${laneHeightVar})`
                            }
                        }) : null
                    ]
                }, t.id);
            }),
            segments.map((seg)=>{
                const hatched = seg.pattern === 'hatched';
                const name = segmentTitle(seg, formatTime);
                return /*#__PURE__*/ _jsx(SpecraTooltip, {
                    content: name,
                    delayDuration: 100,
                    children: /*#__PURE__*/ _jsx(SpecraChartLink, {
                        "aria-label": name,
                        className: cn('absolute flex h-[var(--component-swimlane-segment-height)] items-center overflow-hidden rounded-[var(--radius-xs)]', specraFocusRing, density === 'compact' ? 'top-1/2 -translate-y-1/2' : 'bottom-[var(--space-5)]', hatched && 'border border-dashed border-[color:var(--border-dashed)]'),
                        "data-item-id": seg.id,
                        "data-pattern": hatched ? 'hatched' : 'solid',
                        "data-testid": getSegmentTestId?.(lane, seg),
                        href: seg.href,
                        renderLink: renderLink,
                        role: seg.href ? undefined : 'img',
                        style: {
                            left: `${pos(seg.start)}%`,
                            width: `${pos(seg.end) - pos(seg.start)}%`,
                            ...hatched ? {
                                backgroundImage: chartHatchBackground
                            } : {
                                background: chartToneVar[seg.tone]
                            }
                        },
                        tabIndex: dimmed ? -1 : seg.href ? undefined : 0,
                        children: /*#__PURE__*/ _jsx(SpecraText, {
                            "aria-hidden": "true",
                            className: "truncate px-[var(--space-1-5)] font-medium",
                            size: "micro",
                            style: {
                                color: hatched ? 'var(--text-primary)' : chartToneInk[seg.tone]
                            },
                            children: seg.label
                        })
                    })
                }, seg.id);
            }),
            stations.map((s)=>/*#__PURE__*/ _jsx(Station, {
                    dimmed: dimmed,
                    onStationSelect: onStationSelect,
                    pos: pos,
                    selected: selectedStationId === s.id,
                    station: s,
                    testId: getStationTestId?.(lane, s)
                }, s.id)),
            (lane.markers ?? []).map((m)=>m.kind === 'next-round' ? /*#__PURE__*/ _jsx(SpecraText, {
                    className: "absolute whitespace-nowrap font-medium",
                    "data-marker": m.kind,
                    size: "meta",
                    style: {
                        left: `${pos(m.at)}%`,
                        top: density === 'compact' ? 'calc(50% + var(--space-2))' : 'calc(100% - var(--space-4))'
                    },
                    title: m.title,
                    children: m.label ?? m.title
                }, m.id) : /*#__PURE__*/ _jsx(SpecraTooltip, {
                    content: m.title,
                    delayDuration: 100,
                    children: /*#__PURE__*/ _jsx("span", {
                        "aria-label": m.title,
                        className: cn('absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-xs)]', specraFocusRing),
                        "data-marker": m.kind,
                        role: "img",
                        style: {
                            left: `${pos(m.at)}%`
                        },
                        tabIndex: dimmed ? -1 : 0,
                        children: /*#__PURE__*/ _jsx(SpecraSwatch, {
                            shape: "diamond",
                            tone: "ink"
                        })
                    })
                }, m.id)),
            (lane.annotations ?? []).map((a)=>{
                const row = a.row ?? 'thread';
                return /*#__PURE__*/ _jsx("div", {
                    className: "absolute z-10 max-w-[var(--component-swimlane-label-width)]",
                    "data-annotation": row,
                    style: {
                        left: `${pos(a.at)}%`,
                        top: row === 'thread' ? 0 : row === 'segment' ? 'calc(100% - var(--space-5) - var(--component-swimlane-segment-height))' : '100%'
                    },
                    children: a.node
                }, a.id);
            })
        ]
    });
}
const STATION_LABEL = {
    above: 'bottom-full mb-[var(--space-0-5)] -translate-x-1/2 left-1/2',
    below: 'top-full mt-[var(--space-0-5)] -translate-x-1/2 left-1/2',
    right: 'left-full top-1/2 ml-[var(--space-1)] -translate-y-1/2'
};
function Station({ station: s, selected, dimmed, pos, testId, onStationSelect }) {
    const style = {
        left: `${pos(s.at)}%`,
        top: s.on === 'segment' ? 'calc(100% - var(--space-5) - var(--component-swimlane-segment-height) / 2)' : 'var(--space-6)'
    };
    const glyph = /*#__PURE__*/ _jsx(SpecraSwatch, {
        shape: s.hollow ? 'ring' : 'dot',
        size: selected ? 'md' : 'sm',
        tone: s.tone
    });
    const label = s.label ? /*#__PURE__*/ _jsx(SpecraText, {
        "aria-hidden": "true",
        className: cn('pointer-events-none absolute whitespace-nowrap', STATION_LABEL[s.labelPlacement ?? 'above']),
        size: "meta",
        children: s.label
    }) : null;
    if (onStationSelect) {
        return /*#__PURE__*/ _jsx(SpecraTooltip, {
            content: s.title,
            delayDuration: 100,
            children: /*#__PURE__*/ _jsxs("button", {
                "aria-label": s.title,
                "aria-pressed": selected,
                className: cn('absolute flex -translate-x-1/2 -translate-y-1/2 rounded-full', specraFocusRing, selected && 'ring-2 ring-foreground ring-offset-2 ring-offset-[color:var(--surface-card)]'),
                "data-item-id": s.id,
                "data-testid": testId,
                onClick: ()=>onStationSelect(s.id),
                style: style,
                tabIndex: dimmed ? -1 : undefined,
                type: "button",
                children: [
                    glyph,
                    label
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(SpecraTooltip, {
        content: s.title,
        delayDuration: 100,
        children: /*#__PURE__*/ _jsxs("span", {
            "aria-label": s.title,
            className: cn('absolute flex -translate-x-1/2 -translate-y-1/2 rounded-full', specraFocusRing),
            "data-item-id": s.id,
            "data-testid": testId,
            role: "img",
            style: style,
            tabIndex: dimmed ? -1 : 0,
            children: [
                glyph,
                label
            ]
        })
    });
}
